import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { AnnualReviewFeedbackHr } from '../../../types/AnnualFeedbackReport';

interface HeaderData {
  startDate: string;
  endDate: string;
  mentorDeadline: string;
  progress: string;
}
export const exportToExcel = (
  headerData: HeaderData,
  data: AnnualReviewFeedbackHr,
  fileName: string
) => {
  // Extract category data
  const {
    orgLevelObjectives,
    rolesAndResponsibilities,
    skillAlignments,
    valueAlignmentAssessments,
  } = data.categoryItems;

  // Create description arrays for each category
  const categories = {
    objectives: orgLevelObjectives.map((obj) => obj.description),
    roles: rolesAndResponsibilities.map((role) => role.description),
    skills: skillAlignments.map((skill) => skill.description),
    values: valueAlignmentAssessments.map((value) => value.description),
  };

  // Static column headers
  const staticHeaders = [
    'Mentee Name',
    'Mentor Name',
    'Designation',
    'Tier',
    'Discipline',
    'Mentor Feedback Submission Status',
    'Expected Par Points',
    'Actual Par Points',
    'Client Utilization',
  ];

  // Helper function to find matching item and return scores
  const getScores = (items: any[], description: string) => {
    const item = items?.find(
      (item) =>
        item.description.trim().toLowerCase() ===
        description.trim().toLowerCase()
    );
    return {
      mentor: item?.originalRating ?? 'N/A',
      hr: item?.rating ?? 'N/A',
    };
  };

  // Generate employee data rows
  const employeeReportData = data.annualReviewReportItems.map((emp) => {
    const row: { [key: string]: string | number | null } = {
      'Mentee Name': emp.menteeName,
      'Mentor Name': emp.mentorName,
      Designation: emp.designation,
      Tier: emp.tier,
      Discipline: emp.discipline,
      'Mentor Feedback Submission Status': emp.mentorFeedbackSubmissionStatus,
      'Expected Par Points': emp.expectedParPoints,
      'Actual Par Points': emp.actualParPoints,
      'Client Utilization': emp.clientUtilization
        ? `${emp.clientUtilization}%`
        : '0%',
    };

    // Add scores for each category
    categories.objectives.forEach((desc) => {
      const scores = getScores(emp.orgLevelObjectives, desc);
      row[`${desc}_M`] = scores.mentor;
      row[`${desc}_H`] = scores.hr;
    });

    categories.roles.forEach((desc) => {
      const scores = getScores(emp.rolesAndResponsibilities, desc);
      row[`${desc}_M`] = scores.mentor;
      row[`${desc}_H`] = scores.hr;
    });

    categories.skills.forEach((desc) => {
      const scores = getScores(emp.skillAlignments, desc);
      row[`${desc}_M`] = scores.mentor;
      row[`${desc}_H`] = scores.hr;
    });

    categories.values.forEach((desc) => {
      const scores = getScores(emp.valueAlignmentAssessments, desc);
      row[`${desc}_M`] = scores.mentor;
      row[`${desc}_H`] = scores.hr;
    });

    row['Mentor Recommendation'] = emp.promotionRecommendation ? 'Yes' : 'No';

    return row;
  });

  // Create worksheet and add top header
  const worksheet = XLSX.utils.json_to_sheet([]);
  const topHeader = [
    { a1: 'Start Date', a2: headerData.startDate },
    { a1: 'End Date', a2: headerData.endDate },
    { a1: 'Mentor Deadline', a2: headerData.mentorDeadline },
    { a1: 'Status', a2: headerData.progress },
  ];

  XLSX.utils.sheet_add_json(worksheet, topHeader, {
    origin: 'A1',
    skipHeader: true,
  });

  // Create dynamic headers for each category
  const dynamicHeaders = {
    objectives: categories.objectives.flatMap((desc) => [
      `${desc}_M`,
      `${desc}_H`,
    ]),
    roles: categories.roles.flatMap((desc) => [`${desc}_M`, `${desc}_H`]),
    skills: categories.skills.flatMap((desc) => [`${desc}_M`, `${desc}_H`]),
    values: categories.values.flatMap((desc) => [`${desc}_M`, `${desc}_H`]),
  };

  // Build three-row header structure
  const buildHeaderRows = () => {
    const sectionRow = [
      ...staticHeaders,
      'Org Level Objectives',
      ...new Array(dynamicHeaders.objectives.length - 1).fill(''),
      'Roles and Responsibilities',
      ...new Array(dynamicHeaders.roles.length - 1).fill(''),
      'Skill Alignments',
      ...new Array(dynamicHeaders.skills.length - 1).fill(''),
      'Value Alignment Assessments',
      ...new Array(dynamicHeaders.values.length - 1).fill(''),
      'Recommended for promotion',
    ];

    const statementRow = [
      ...staticHeaders.map(() => ''),
      ...categories.objectives.flatMap((desc) => [desc, '']),
      ...categories.roles.flatMap((desc) => [desc, '']),
      ...categories.skills.flatMap((desc) => [desc, '']),
      ...categories.values.flatMap((desc) => [desc, '']),
      'mentor recommendation',
    ];

    const scoreRow = [
      ...staticHeaders.map(() => ''),
      ...categories.objectives.flatMap(() => ['Mentor Score', 'HR Score']),
      ...categories.roles.flatMap(() => ['Mentor Score', 'HR Score']),
      ...categories.skills.flatMap(() => ['Mentor Score', 'HR Score']),
      ...categories.values.flatMap(() => ['Mentor Score', 'HR Score']),
      '',
    ];

    return [sectionRow, statementRow, scoreRow];
  };

  XLSX.utils.sheet_add_aoa(worksheet, buildHeaderRows(), { origin: 'A6' });

  // Create cell merges
  const createMerges = () => {
    const merges: { s: { r: number; c: number }; e: { r: number; c: number } }[] = [];
    let colOffset = staticHeaders.length;

    // Section header merges
    const sections = [
      { length: dynamicHeaders.objectives.length, name: 'objectives' },
      { length: dynamicHeaders.roles.length, name: 'roles' },
      { length: dynamicHeaders.skills.length, name: 'skills' },
      { length: dynamicHeaders.values.length, name: 'values' },
    ];

    sections.forEach((section) => {
      merges.push({
        s: { r: 5, c: colOffset },
        e: { r: 5, c: colOffset + section.length - 1 },
      });
      colOffset += section.length;
    });

    // Static headers vertical merge
    staticHeaders.forEach((_, index) => {
      merges.push({ s: { r: 5, c: index }, e: { r: 7, c: index } });
    });

    // Statement header merges
    colOffset = staticHeaders.length;
    [
      categories.objectives,
      categories.roles,
      categories.skills,
      categories.values,
    ].forEach((categoryItems) => {
      categoryItems.forEach((_, index) => {
        merges.push({
          s: { r: 6, c: colOffset + index * 2 },
          e: { r: 6, c: colOffset + index * 2 + 1 },
        });
      });
      colOffset += categoryItems.length * 2;
    });

    // Merge promotion recommendation header
    merges.push({ s: { r: 5, c: colOffset }, e: { r: 6, c: colOffset } });


    return merges;
  };

  worksheet['!merges'] = createMerges();

  // Add employee data
  XLSX.utils.sheet_add_json(worksheet, employeeReportData, {
    origin: 'A9',
    skipHeader: true,
  });

  // Create and save workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Annual Feedback Report');
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
  saveAs(blob, `${fileName}.xlsx`);
};